/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  SelectMobileFieldInput,
  isBeforeDataFetch,
  isPhone,
  isTablet
} from '#PRIVATE_SelectCommon';
import { DataState, ListItemRendererContext, Metadata } from '#UNSAFE_Collection';
import { Label } from '#UNSAFE_Label';
import { ReadonlyTextField, ReadonlyTextFieldInput } from '#UNSAFE_TextField';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  UserAssistanceDensityType
} from '#UNSAFE_UserAssistance';
import { VirtualizedListView } from '#UNSAFE_VirtualizedListView';
import { FocusableHandle, useFocusableTextField } from '#hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { useLoadingIndicatorTimer } from '#hooks/UNSAFE_useLoadingIndicatorTimer';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { Item } from '#utils/UNSAFE_dataProvider';
import { ItemTextType } from '#utils/UNSAFE_selectUtils';
import { Size } from '#utils/UNSAFE_size';
import { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { ComponentChildren, ComponentProps, Fragment, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { MutableRef } from 'preact/hooks';
import { SelectSingleDesktopDropdown } from './SelectSingleDesktopDropdown';
import { SelectSingleField } from './SelectSingleField';
import { SelectSingleFieldInput } from './SelectSingleFieldInput';
import { SelectSingleMobileDropdown } from './SelectSingleMobileDropdown';
import { useDisplayValue } from './useDisplayValue';
import { useSelectSingle } from './useSelectSingle';

type SelectSingleFieldInputProps = ComponentProps<typeof SelectSingleFieldInput>;
type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

// pick the props from the ListItemContext instead of extending it directly so that we
// don't automatically expose any new props that may be added to it
type PickedPropsFromListItemContext<K, D> = Pick<ListItemRendererContext<K, D>, 'data' | 'index'>;
type SelectionDetail<K> = {
  value: Set<K>;
  target: EventTarget | null;
};
type ItemRendererMetadata<K> = Pick<Metadata<K>, 'key' | 'suggestion'>;
type ItemRendererProps<K, D> = PickedPropsFromListItemContext<K, D> & {
  /**
   * Metadata for the item.
   */
  metadata: ItemRendererMetadata<K>;
  /**
   * User-entered search text.
   * This property will be undefined for the default, unfiltered list.
   */
  searchText?: string;
  /**
   * The selected keys in the dropdown.
   * This property will be undefined when nothing is selected.
   */
  selectedKeys?: Set<K>;
  /**
   * Callback function to trigger when the selection changes
   *
   * @param detail The selection change details
   */
  onSelectionChange: (detail: SelectionDetail<K>) => void;
};

type Props<K extends string | number, D extends Record<string, any>> = TestIdProps & {
  /**
   * Specifies whether the add to list link is shown in the dropdown when the user filters
   * and there are no results.
   * It is up to the application to show its own UI when the link is triggered.
   *
   * Advanced search and add to list should not be enabled at the same time.  If both are on,
   * advanced search will take precedence because the conditions for when it is shown are
   * more relaxed than add to list and its external UI could also potentially include the
   * ability to add to list.
   */
  addToList?: 'on' | 'off';
  /**
   * Specifies whether the advanced search link is shown in the dropdown when the user filters.
   * It is up to the application to show its own UI when the link is triggered.
   *
   * Advanced search and add to list should not be enabled at the same time.  If both are on,
   * advanced search will take precedence because the conditions for when it is shown are
   * more relaxed than add to list and its external UI could also potentially include the
   * ability to add to list.
   */
  advancedSearch?: 'on' | 'off';
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: SelectSingleFieldInputProps['aria-describedby'];
  /**
   * Text to provide guidance to help the user understand what data to enter or select.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;
  /**
   * Specifies data for the dropdown list.
   *
   * Note that the <code>itemText</code> property allows for customizing the rendering
   * of each data item.
   */
  data?: DataState<K, D> | null;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies whether the component is waiting for <code>valueItems</code> to load.
   * While loading, SelectSingle will show the progressive loading indicator in the text field.
   */
  isLoading?: boolean;
  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;
  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * selected by the user.  Setting this property to <code>true</code> means that a value
   * is required to be selected by the user.
   */
  isRequired?: SelectSingleFieldInputProps['isRequired'];
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has selected a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];
  /**
   * Render function for an item in the default list in the dropdown.
   */
  itemRenderer?: (itemRendererProps: ItemRendererProps<K, D>) => ComponentChildren;
  /**
   * Specifies how to get the text string to render for a data item.
   * This property can be set to either:
   * <ul>
   * <li>a string that specifies the name of a top level data attribute to render as text, or</li>
   * <li>a callback function that takes a properties object and returns the text string to
   * display.</li>
   * </ul>
   *
   * This text will be rendered for the selected <code>valueItems</code> of the component.
   * It will also be rendered for each item in the dropdown.  When rendered for the
   * dropdown items, default matching search term highlighting will still be applied.
   */
  itemText: ItemTextType<K, D>;
  /**
   * Specifies the label associated with the field.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * A short hint to display before the user selects or enters a value.
   */
  placeholder?: SelectSingleFieldInputProps['placeholder'];
  /**
   * Specifies how to align text within the field.
   */
  textAlign?: SelectSingleFieldInputProps['textAlign'];
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Specifies the key, data, and optional metadata for the selected value.
   */
  valueItem?: Item<K, D>;
  /**
   * The style variant of the component.
   */
  variant?: SelectSingleFieldInputProps['variant'];
  /**
   * The type of virtual keyboard to display for entering a value on mobile browsers.
   * This property has no effect on desktop browsers.
   */
  virtualKeyboard?: 'auto' | 'email' | 'number' | 'search' | 'tel' | 'text' | 'url';
  /**
   * Callback function called when the user triggers the add to list link shown in the dropdown.
   */
  onAddToListAction?: ({ searchText }: { searchText?: string }) => void;
  /**
   * Callback function called when the user triggers the advanced search link shown in the dropdown.
   */
  onAdvancedSearchAction?: ({ searchText }: { searchText?: string }) => void;
  /**
   * Callback invoked when the user commits the selected value.
   *
   * The function should set a new <code>valueItem</code> property.
   */
  onCommit: (detail: ValueUpdateDetail<K>) => void;
  /**
   * Callback function to trigger loading data for the dropdown list, which may or may not be
   * filtered by user entered search text.
   *
   * The function should set a new <code>DataState</code> as the <code>data</code> property.
   */
  onFilter?: ({ searchText }: { searchText?: string }) => void;
  /**
   * Callback function to handle when the viewport of the dropdown list has changed, for example
   * by scrolling, including the case where the user scrolls to the end of the list and there are
   * more items to load.
   *
   * The function should set a new <code>DataState</code> for the specified range as the
   * <code>data</code> property.
   */
  onLoadRange?: ComponentProps<typeof VirtualizedListView>['onLoadRange'];
};

/**
 * A SelectSingle is a dropdown list that supports single selection and search filtering.
 */
export const SelectSingle = forwardRef(
  <K extends string | number, D extends Record<string, any>>(
    {
      addToList = 'off',
      advancedSearch = 'off',
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      data,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isLoading: propIsLoading,
      isReadonly: propIsReadonly,
      isRequired,
      isRequiredShown,
      itemRenderer,
      itemText,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      onAddToListAction,
      onAdvancedSearchAction,
      onCommit,
      onFilter,
      onLoadRange,
      placeholder,
      testId,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      valueItem,
      variant = 'default',
      virtualKeyboard
    }: Props<K, D>,
    ref?: Ref<FocusableHandle>
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const {
      enabledElementRef,
      focusProps,
      isFocused: origIsFocused,
      readonlyElementRef
    } = useFocusableTextField<HTMLInputElement, HTMLDivElement>({ isDisabled, isReadonly, ref });

    const {
      addToListEventHandlers,
      advancedSearchEventHandlers,
      clearValue,
      collectionProps,
      dropdownArrowEventHandlers,
      dropdownEventHandlers,
      dropdownRef,
      inputEventHandlers,
      isAddToListShown,
      isAdvancedSearchShown,
      isDataFetched,
      isDropdownAbove,
      isDropdownOpen,
      isEmptyResults,
      isFocused,
      isUserFiltering,
      mainFieldRef,
      mouseProps,
      searchText,
      setDropdownOpen,
      setUserInput,
      stopFiltering,
      textFieldRef,
      userInput
    } = useSelectSingle<K, D>({
      addToList,
      advancedSearch,
      data,
      inputRef: enabledElementRef as MutableRef<HTMLInputElement>,
      isDisabled,
      isFocused: origIsFocused,
      isReadonly,
      onAddToListAction,
      onAdvancedSearchAction,
      onCommit,
      onFilter,
      valueItem
    });

    // The incoming propIsLoading tells us that we are in a loading state, but we don't want to
    // show the loading indicator until after a delay, because showing it immediately could result
    // in unwanted flashing.  The isLoading var below will be set to true by useLoadingIndicatorTimer
    // after the delay, when the timer expires, at which point we should show the loading indicator.
    // If propIsLoading is set to false before the timer expires, then the timer will be cancelled
    // and no loading indicator will be shown.
    // (ListView detects its loading state by checking data === null.  While it would be nice for
    // us to be consistent and check valueItems === null, we need to use a separate isLoading prop.
    // If valueItems === null triggers our loading state, then the selections in the dropdown
    // are cleared for that period of time, which is undesirable.)
    const isLoading = useLoadingIndicatorTimer(propIsLoading ?? false);

    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused,
      isLoading,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value: valueItem !== undefined ? true : undefined
    });

    // JET-54256 - inside label animates up on initial display with selected value
    // If we're waiting for the selected values to load, the component should look like it
    // has a value in the meantime.
    // Use the original propIsLoading instead of isLoading because we don't want to delay
    // the inside label position by a timer like we do the progressive loading indicator.
    formFieldContext.hasValue = propIsLoading || formFieldContext.hasValue;

    const dropdownId = `${baseId}-dropdown`;

    const displayValue = useDisplayValue(itemText, valueItem);

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={mainFieldRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onfocusout}
            onFocus={focusProps.onfocusin}
            ref={mainFieldRef}
            testId={testId}
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              aria-describedby={inputProps['aria-describedby']}
              aria-label={ariaLabel}
              aria-labelledby={labelProps.id}
              as="div"
              elementRef={readonlyElementRef}
              hasEmptyLabel={label === '' && labelEdge === 'none'}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
              textAlign={textAlign}
              value={displayValue}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    const hasInsideLabel = labelComp !== undefined && labelEdge === 'inside';
    const liveRegionText =
      isDropdownOpen && isDataFetched
        ? data?.totalSize === 0
          ? translations.select_noMatchesFound()
          : data?.totalSize === 1
          ? translations.select_oneMatchFound()
          : data?.sizePrecision === 'exact'
          ? translations.select_sizeMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
          : translations.select_sizeOrMoreMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
        : '';

    const hasEmptyLabel = label === '' && labelEdge === 'none';
    // Use the mobile field if the component is being rendered in a phone
    // and it is not disabled.
    const isPhoneMainFieldInput = isPhone() && !isDisabled;
    const mainContent = !isPhoneMainFieldInput ? (
      <SelectSingleFieldInput
        aria-controls={dropdownId}
        aria-expanded={isDropdownOpen}
        aria-label={ariaLabel}
        displayValue={displayValue}
        hasEmptyLabel={label === '' && labelEdge === 'none'}
        hasInsideLabel={hasInsideLabel}
        inputRef={enabledElementRef}
        isAddToListShown={isAddToListShown}
        isAdvancedSearchShown={isAdvancedSearchShown}
        isRequired={isRequired}
        isUserFiltering={isUserFiltering}
        liveRegionText={liveRegionText}
        placeholder={placeholder}
        textAlign={textAlign}
        userInput={userInput}
        // In tablet, we would want to show the virtual keyboard
        virtualKeyboard={isTablet() ? virtualKeyboard : undefined}
        {...inputProps}
        {...inputEventHandlers}
      />
    ) : (
      <SelectMobileFieldInput
        aria-controls={dropdownId}
        aria-describedby={inputProps['aria-describedby']}
        aria-expanded={isDropdownOpen}
        aria-invalid={inputProps['aria-invalid']}
        aria-label={ariaLabel}
        aria-labelledby={labelProps.id}
        displayValue={displayValue}
        hasEmptyLabel={hasEmptyLabel}
        hasInsideLabel={hasInsideLabel}
        isRequired={isRequired}
        onBlur={inputProps.onBlur}
        onFocus={inputProps.onFocus}
        placeholder={placeholder}
        ref={enabledElementRef as Ref<HTMLDivElement>}
        textAlign={textAlign}
        variant={inputProps.variant}
      />
    );

    const shouldShowDropdown =
      isDropdownOpen &&
      data !== undefined &&
      (data === null || data.totalSize > 0 || isBeforeDataFetch<K, D>(data) || isEmptyResults);
    const dropdown = !shouldShowDropdown ? null : isPhone() ? (
      <SelectSingleMobileDropdown
        {...collectionProps}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        assistiveText={assistiveText}
        clearValue={clearValue}
        data={data}
        displayValue={displayValue}
        dropdownId={dropdownId}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        isAddToListShown={isAddToListShown}
        isAdvancedSearchShown={isAdvancedSearchShown}
        isEmptyResults={isEmptyResults}
        isFocused={isFocused}
        isLoading={isLoading}
        isOpen={shouldShowDropdown}
        isRequired={isRequired}
        isUserFiltering={isUserFiltering}
        itemRenderer={itemRenderer}
        itemText={itemText}
        label={label}
        liveRegionText={liveRegionText}
        mainFieldInputRef={enabledElementRef}
        onAddToListAction={addToListEventHandlers.onAction}
        onAdvancedSearchAction={advancedSearchEventHandlers.onAction}
        onFieldBlur={focusProps.onfocusout}
        onFieldFocus={focusProps.onfocusin}
        onFieldInput={inputEventHandlers.onInput}
        onFieldKeyDown={inputEventHandlers.onKeyDown}
        onFieldKeyUp={inputEventHandlers.onKeyUp}
        onLoadRange={onLoadRange}
        placeholder={placeholder}
        propIsLoading={propIsLoading}
        searchText={searchText}
        setDropdownOpen={setDropdownOpen}
        stopFiltering={stopFiltering}
        setUserInput={setUserInput}
        textAlign={textAlign}
        userAssistanceDensity={userAssistanceDensity}
        userInput={userInput}
        virtualKeyboard={virtualKeyboard}
      />
    ) : (
      <SelectSingleDesktopDropdown
        {...collectionProps}
        {...dropdownEventHandlers}
        assistiveText={assistiveText}
        data={data}
        dropdownId={dropdownId}
        dropdownRef={dropdownRef}
        helpSourceLink={helpSourceLink}
        helpSourceText={helpSourceText}
        isAddToListShown={isAddToListShown}
        isAdvancedSearchShown={isAdvancedSearchShown}
        isDisabled={isDisabled}
        isDropdownAbove={isDropdownAbove}
        isEmptyResults={isEmptyResults}
        isOpen={shouldShowDropdown}
        isReadonly={isReadonly}
        isUserFiltering={isUserFiltering}
        itemRenderer={itemRenderer}
        itemText={itemText}
        label={label}
        mainFieldRef={mainFieldRef}
        onAddToListAction={addToListEventHandlers.onAction}
        onAdvancedSearchAction={advancedSearchEventHandlers.onAction}
        onLoadRange={onLoadRange}
        searchText={searchText}
        userAssistanceDensity={userAssistanceDensity}
      />
    );

    return (
      <Fragment>
        <FormFieldContext.Provider value={formFieldContext}>
          <SelectSingleField
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            hasInsideLabel={hasInsideLabel}
            inlineUserAssistance={inlineUserAssistance}
            isDropdownArrowShown={true}
            mainFieldRef={mainFieldRef}
            onBlur={focusProps?.onfocusout}
            onDropdownArrowClick={dropdownArrowEventHandlers.onClick}
            onFocus={focusProps?.onfocusin}
            rootRef={textFieldRef as Ref<HTMLElement> | undefined}
            testId={testId}
            {...textFieldProps}
            {...fieldLabelProps}
            {...mouseProps}>
            {mainContent}
          </SelectSingleField>
        </FormFieldContext.Provider>
        {dropdown}
      </Fragment>
    );
  }
);
